import { useParams } from 'react-router-dom';

import { useAuth } from '../../../../../../core/auth';
import { useAppDispatch, useAppSelector } from '../../../../../../lib/store';
import { selectBatchId } from '../../../../../supervisor-queue/state/selector';
import type { Annotation } from '../../../../../workspace-common/types/responseTypes';
import { useGetSupervisorWorkspaceQuery } from '../../../../api/supervisorApiSlice';
import { SUPERVISOR_REVIEW_TAB_OPTIONS } from '../../../../constants';
import { useSupervisorMode } from '../../../../context/SupervisorModeContext';
import {
  selectAnnotatorOneAllConceptsExpanded,
  selectAnnotatorOneExpandedConceptIds,
  selectAnnotatorTwoExpandedConceptIds,
  selectAnnotatorTwoAllConceptsExpanded,
  selectReviewTabSelected,
} from '../../../../state/selector';
import {
  setAnnotatorOneAllConceptsExpanded,
  setAnnotatorTwoAllConceptsExpanded,
  addToAnnotatorOneExpandedConceptIds,
  addToAnnotatorTwoExpandedConceptIds,
  removeFromAnnotatorOneExpandedConceptIds,
  removeFromAnnotatorTwoExpandedConceptIds,
} from '../../../../state/supervisorSlice';

interface AnnotatorData {
  conceptId: string;
  conceptName: string;
  annotations: Annotation[];
}

interface useAnnotatorAnnotationsTabResults {
  allConceptsExpanded: boolean | undefined;
  expandedConceptIds: string[];
  setAllConceptsExpanded: () => void;
  clearAllConceptsExpanded: () => void;
  addToExpandedConceptIds: (conceptId: string) => void;
  removeFromExpandedConceptIds: (conceptId: string) => void;
  annotatorData: AnnotatorData[];
  isDataLoading: boolean;
}

export const useAnnotatorAnnotationsTab = (): useAnnotatorAnnotationsTabResults => {
  const { encounterId } = useParams();
  const dispatch = useAppDispatch();
  const selectedReviewTab = useAppSelector(selectReviewTabSelected);
  const { user } = useAuth();
  const { isReadOnly } = useSupervisorMode();
  const batchId = useAppSelector(selectBatchId);
  const {
    data: supervisorWorkspace,
    isLoading: isSupervisorWorkspaceLoading,
    isFetching: isSupervisorWorkspaceFetching,
  } = useGetSupervisorWorkspaceQuery(
    { encounterId: encounterId as string, batchId: batchId as string },
    { skip: !user.isUser && !isReadOnly }
  );
  const conceptNameMap: Record<string, string> = {};
  for (const concept of supervisorWorkspace?.data.concepts ?? []) {
    conceptNameMap[concept.conceptId] = concept.conceptName;
  }

  const buildAnnotatorData = (annotations: Annotation[]): AnnotatorData[] => {
    const byConceptId: Record<string, Annotation[]> = {};
    for (const ann of annotations) {
      if (!byConceptId[ann.conceptId]) byConceptId[ann.conceptId] = [];
      byConceptId[ann.conceptId].push(ann);
    }
    return Object.entries(byConceptId).map(([conceptId, anns]) => ({
      conceptId,
      conceptName: conceptNameMap[conceptId] ?? conceptId,
      annotations: anns,
    }));
  };

  const annotator1Data = buildAnnotatorData(
    supervisorWorkspace?.data.annotators[0]?.annotations ?? []
  );
  const annotator2Data = buildAnnotatorData(
    supervisorWorkspace?.data.annotators[1]?.annotations ?? []
  );

  const annotatorOneAllConceptsExpanded = useAppSelector(selectAnnotatorOneAllConceptsExpanded);
  const annotatorTwoAllConceptsExpanded = useAppSelector(selectAnnotatorTwoAllConceptsExpanded);
  const annotatorOneExpandedConceptIds = useAppSelector(selectAnnotatorOneExpandedConceptIds);
  const annotatorTwoExpandedConceptIds = useAppSelector(selectAnnotatorTwoExpandedConceptIds);

  const annotatorOneSetAllConceptsExpanded = () => {
    dispatch(setAnnotatorOneAllConceptsExpanded(true));
  };
  const annotatorTwoSetAllConceptsExpanded = () => {
    dispatch(setAnnotatorTwoAllConceptsExpanded(true));
  };
  const annotatorOneClearAllConceptsExpanded = () => {
    dispatch(setAnnotatorOneAllConceptsExpanded(false));
  };
  const annotatorTwoClearAllConceptsExpanded = () => {
    dispatch(setAnnotatorTwoAllConceptsExpanded(false));
  };
  const annotatorOneAddToExpandedConceptIds = (conceptId: string) => {
    dispatch(addToAnnotatorOneExpandedConceptIds(conceptId));
  };
  const annotatorTwoAddToExpandedConceptIds = (conceptId: string) => {
    dispatch(addToAnnotatorTwoExpandedConceptIds(conceptId));
  };
  const annotatorOneRemoveFromExpandedConceptIds = (conceptId: string) => {
    dispatch(removeFromAnnotatorOneExpandedConceptIds(conceptId));
  };
  const annotatorTwoRemoveFromExpandedConceptIds = (conceptId: string) => {
    dispatch(removeFromAnnotatorTwoExpandedConceptIds(conceptId));
  };

  switch (selectedReviewTab) {
    case SUPERVISOR_REVIEW_TAB_OPTIONS[0]:
      return {
        allConceptsExpanded: annotatorOneAllConceptsExpanded,
        expandedConceptIds: annotatorOneExpandedConceptIds,
        setAllConceptsExpanded: annotatorOneSetAllConceptsExpanded,
        clearAllConceptsExpanded: annotatorOneClearAllConceptsExpanded,
        addToExpandedConceptIds: annotatorOneAddToExpandedConceptIds,
        removeFromExpandedConceptIds: annotatorOneRemoveFromExpandedConceptIds,
        annotatorData: annotator1Data,
        isDataLoading: isSupervisorWorkspaceFetching || isSupervisorWorkspaceLoading,
      };
    case SUPERVISOR_REVIEW_TAB_OPTIONS[1]:
      return {
        allConceptsExpanded: annotatorTwoAllConceptsExpanded,
        expandedConceptIds: annotatorTwoExpandedConceptIds,
        setAllConceptsExpanded: annotatorTwoSetAllConceptsExpanded,
        clearAllConceptsExpanded: annotatorTwoClearAllConceptsExpanded,
        addToExpandedConceptIds: annotatorTwoAddToExpandedConceptIds,
        removeFromExpandedConceptIds: annotatorTwoRemoveFromExpandedConceptIds,
        annotatorData: annotator2Data,
        isDataLoading: isSupervisorWorkspaceFetching || isSupervisorWorkspaceLoading,
      };
    default:
      return {
        allConceptsExpanded: undefined,
        expandedConceptIds: [],
        setAllConceptsExpanded: () => {
          /* noop */
        },
        clearAllConceptsExpanded: () => {
          /* noop */
        },
        addToExpandedConceptIds: () => {
          /* noop */
        },
        removeFromExpandedConceptIds: () => {
          /* noop */
        },
        annotatorData: [] as AnnotatorData[],
        isDataLoading: false,
      };
  }
};
